import { supabase } from "@/lib/supabase";

export type UserRole = "manager" | "candidate" | "recruiter" | "admin" | "participant";

/**
 * Landing page picker used after a successful auth (email OTP, Telegram
 * Login Widget — anything that mints a Supabase session). Single source
 * of truth so all sign-in paths agree on where to send the user.
 *
 *   - organizer-ish roles → "/events" (their list of created events)
 *   - participant → "/me/meetups" (their list of joined meetups)
 *   - null / unknown → "/me/meetups" (safe default; the user can navigate
 *     to /events themselves if they're an organizer)
 */
export function defaultLandingFor(role: UserRole | null): string {
  if (role === "manager" || role === "recruiter" || role === "admin") {
    return "/events";
  }
  return "/me/meetups";
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthError";
  }
}

/**
 * @deprecated Password sign-in is no longer surfaced in the UI — the
 *   global flow is passwordless OTP via requestOtp/verifyOtp. Kept as a
 *   no-throw escape hatch in case we ever need it for admin tooling.
 */
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new AuthError(error.message);
  return data.user;
}

/**
 * @deprecated Sign-up is now done by entering an email on /sign-in —
 *   Supabase auto-creates the user via signInWithOtp({ shouldCreateUser: true }).
 *   Kept for backwards compatibility only.
 */
export async function signUp(email: string, password: string, fullName: string) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: "manager",
        full_name: fullName,
      },
      emailRedirectTo: `${origin}/events`,
    },
  });
  if (error) throw new AuthError(error.message);
  return data.user;
}

/**
 * Send a 6-digit OTP code to the participant's email. Creates the auth user
 * on the first call (handle_new_auth_user trigger fills defaults). Idempotent
 * — repeat calls just send a new code.
 */
export async function requestOtp(email: string): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: true },
  });
  if (error) throw new AuthError(error.message);
}

/**
 * Verify the 6-digit code the participant typed in. Establishes a session.
 */
export async function verifyOtp(email: string, code: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token: code,
    type: "email",
  });
  if (error) throw new AuthError(error.message);
  return { user: data.user, session: data.session };
}

/**
 * Upload an avatar file to meetup-avatars/<uid>/<random>.<ext> and return
 * the public URL. Caller must already be authenticated.
 */
export async function uploadAvatar(file: File): Promise<string> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) throw new AuthError("Not authenticated");
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const path = `${u.user.id}/${crypto.randomUUID()}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from("meetup-avatars")
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) throw new AuthError(upErr.message);
  const { data } = supabase.storage.from("meetup-avatars").getPublicUrl(path);
  return data.publicUrl;
}

/**
 * After OTP verify — set the user's name and (optionally) avatar URL.
 * Idempotently grants the participant role.
 */
export async function completeOnboarding(
  fullName: string,
  avatarUrl?: string,
): Promise<void> {
  const { error } = await supabase.rpc("complete_meetup_onboarding", {
    p_full_name: fullName,
    p_avatar_url: avatarUrl ?? null,
  });
  if (error) throw new AuthError(error.message);
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) return null;
  return data.user;
}

export async function setActiveRole(role: UserRole): Promise<void> {
  const { error } = await supabase.rpc("set_active_role", { p_role: role });
  if (error) throw new AuthError(error.message);
}

export async function addRoleParticipant(): Promise<void> {
  const { error } = await supabase.rpc("add_role_participant");
  if (error) throw new AuthError(error.message);
}

export async function addRoleManager(
  companyName: string,
  fullName?: string,
): Promise<void> {
  const { error } = await supabase.rpc("add_role_manager", {
    p_company_name: companyName,
    p_full_name: fullName ?? null,
  });
  if (error) throw new AuthError(error.message);
}
